// Notifications SMS — /sms-notifications. The SMS a tenant's parcel
// recipients receive about their tracking (delay detected, moving again,
// delivered), the sender they see (Unanimity, or the company once OVH
// validated its documents), a test send and the latest sends
// (Backend docs/customer-sms.md). No phone number to provide: SMS leave from
// Unanimity's OVH account.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import SenderSection from './SenderSection';
import NotificationsSection from './NotificationsSection';
import ActivitySection from './ActivitySection';
import SmsPreview from './SmsPreview';
import { EVENTS, fail, get, put, renderSms } from './api';
import './CustomerSms.css';

const draftFrom = (view) => ({
    sender_mode: view.sender_mode,
    language: view.language,
    events: Object.fromEntries(EVENTS.map((event) => {
        const { enabled, text, only_after_problem: onlyAfter } = view.events[event];
        return [event, { enabled, text: text || null, ...(event === 'parcel_delivered' ? { only_after_problem: onlyAfter !== false } : {}) }];
    })),
});

const CustomerSms = () => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'fr';
    const [view, setView] = useState(null);
    const [baseline, setBaseline] = useState(null);
    const [draft, setDraft] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loadError, setLoadError] = useState('');
    const [saving, setSaving] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [focusEvent, setFocusEvent] = useState('parcel_problem');

    const applyView = useCallback((next, { keepDraft = false } = {}) => {
        setView(next);
        const fresh = draftFrom(next);
        setBaseline(fresh);
        // Sender actions answer the whole view: keep unsaved texts/toggles.
        setDraft((prev) => (keepDraft && prev ? { ...prev, sender_mode: fresh.sender_mode } : fresh));
    }, []);

    const loadMessages = useCallback(async () => {
        try {
            const res = await get('/messages', { limit: 30 });
            setMessages(res.data.items || []);
        } catch (e) {
            setMessages([]);
        }
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const res = await get('/settings');
                applyView(res.data);
            } catch (err) {
                setLoadError(fail(err, t('customerSms.loadError', 'Impossible de charger les réglages SMS.')));
            }
        })();
        loadMessages();
    }, [applyView, loadMessages, t]);

    const dirty = useMemo(() => draft && baseline && JSON.stringify(draft) !== JSON.stringify(baseline), [draft, baseline]);

    const save = async () => {
        setSaving(true);
        setFeedback(null);
        try {
            // A text equal to the default is no custom text (it follows the language).
            const events = Object.fromEntries(EVENTS.map((event) => {
                const item = draft.events[event];
                const defaultText = view.events[event].default_text?.[draft.language];
                const text = item.text && item.text.trim() && item.text.trim() !== defaultText ? item.text.trim() : null;
                return [event, { ...item, text }];
            }));
            const res = await put('/settings', { ...draft, events });
            applyView(res.data);
            setFeedback({ tone: 'success', text: t('customerSms.saved', 'Réglages enregistrés.') });
        } catch (err) {
            setFeedback({ tone: 'danger', text: fail(err, t('customerSms.saveError', "Les réglages n'ont pas pu être enregistrés.")) });
        } finally {
            setSaving(false);
        }
    };

    if (loadError) {
        return <div className="csms-page"><p className="csms-callout csms-callout--danger" role="alert">{loadError}</p></div>;
    }
    if (!view || !draft) {
        return <div className="csms-page"><p className="csms-empty">{t('customerSms.loading', 'Chargement…')}</p></div>;
    }

    const own = view.sender.own;
    const previewSender = draft.sender_mode === 'own' && own?.status === 'active' ? own.name : view.sender.platform.name;
    const focusConfig = draft.events[focusEvent];
    const focusDefault = view.events[focusEvent].default_text?.[draft.language] || '';
    const previewText = renderSms(focusConfig.text?.trim() ? focusConfig.text : focusDefault, view.samples);
    const usage = view.usage || {};
    const enabledCount = EVENTS.filter((event) => draft.events[event].enabled).length;

    return (
        <div className="csms-page">
            <header className="csms-header">
                <div>
                    <h1>{t('customerSms.title', 'Notifications SMS')}</h1>
                    <p>{t('customerSms.subtitle', 'Prévenez les destinataires de vos colis par SMS : retard détecté, colis reparti, colis livré.')}</p>
                </div>
                <div className="csms-usage" aria-label={t('customerSms.usage.label', 'SMS de ce mois')}>
                    <span className="csms-usage-value">{(usage.sent || 0).toLocaleString(lang)}</span>
                    <span className="csms-usage-label">{t('customerSms.usage.sent', 'SMS envoyés ce mois')}</span>
                    {(usage.failed > 0 || usage.skipped > 0) && (
                        <span className="csms-usage-sub">
                            {[usage.failed > 0 && t('customerSms.usage.failed', '{{n}} refusés', { n: usage.failed }),
                              usage.skipped > 0 && t('customerSms.usage.skipped', '{{n}} non envoyés', { n: usage.skipped })]
                                .filter(Boolean).join(' · ')}
                        </span>
                    )}
                    <span className="csms-usage-sub">
                        {t('customerSms.usage.active', '{{n}} notification(s) activée(s) sur 3', { n: enabledCount })}
                    </span>
                </div>
            </header>

            {!view.ovh_configured && (
                <p className="csms-callout csms-callout--warning" role="status">
                    <FaExclamationTriangle aria-hidden="true" />
                    <span>{t('customerSms.banner.notConfigured', "L'envoi de SMS n'est pas encore configuré sur la plateforme : contactez le support.")}</span>
                </p>
            )}
            {!view.parcel_monitoring && (
                <p className="csms-callout csms-callout--info" role="status">
                    <FaInfoCircle aria-hidden="true" />
                    <span>
                        {t('customerSms.banner.noMonitoring', 'Les SMS partent pour les colis suivis par la surveillance colis, désactivée sur votre compte : aucun SMS ne sera envoyé.')}
                        {' '}<Link to="/parcel-monitor" className="csms-link">{t('customerSms.banner.openMonitoring', 'Ouvrir la surveillance colis')}</Link>
                    </span>
                </p>
            )}
            {view.sender.effective && !view.sender.effective.ready && (
                <p className="csms-callout csms-callout--warning" role="status">
                    <FaExclamationTriangle aria-hidden="true" />
                    <span>{t('customerSms.banner.platformInactive', "L'expéditeur « {{name}} » n'est pas encore actif chez OVH : les SMS ne partent pas encore.", { name: view.sender.platform.name })}</span>
                </p>
            )}
            {feedback && (
                <p className={`csms-callout csms-callout--${feedback.tone}`} role="status">
                    <span>{feedback.text}</span>
                    <button type="button" className="csms-link" onClick={() => setFeedback(null)}>{t('customerSms.close', 'Fermer')}</button>
                </p>
            )}

            <div className="csms-layout">
                <div className="csms-main">
                    <SenderSection view={view} senderMode={draft.sender_mode} lang={lang} setFeedback={setFeedback}
                        onModeChange={(mode) => setDraft((prev) => ({ ...prev, sender_mode: mode }))}
                        onViewChange={(next) => { applyView(next, { keepDraft: true }); }} />
                    <NotificationsSection view={view} draft={draft} focusEvent={focusEvent} setFocusEvent={setFocusEvent}
                        onDraft={(patch) => setDraft((prev) => ({ ...prev, ...patch }))} />
                    <ActivitySection view={view} messages={messages} onSent={loadMessages} lang={lang} />
                </div>
                <aside className="csms-aside">
                    <SmsPreview sender={previewSender} text={previewText} enabled={focusConfig.enabled}
                        eventLabel={t(`customerSms.events.${focusEvent}.title`, focusEvent)} />
                </aside>
            </div>

            {dirty && view.can_edit && (
                <div className="csms-savebar" role="region" aria-label={t('customerSms.unsaved', 'Modifications non enregistrées')}>
                    <span>{t('customerSms.unsaved', 'Modifications non enregistrées')}</span>
                    <div className="csms-actions">
                        <button type="button" className="u-btn u-btn--secondary" onClick={() => setDraft(baseline)} disabled={saving}>
                            {t('customerSms.cancel', 'Annuler')}
                        </button>
                        <button type="button" className="u-btn u-btn--primary" onClick={save} disabled={saving}>
                            {saving ? t('customerSms.saving', 'Enregistrement…') : t('customerSms.save', 'Enregistrer')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomerSms;
